import { useEffect, useState } from 'react';
import {
  HEX_RE, isGradient, parseGradient, formatGradient, type DegradeParsed,
} from '../lib/cor-tema';

interface Props {
  valor: string;
  onChange: (valor: string) => void;
}

// Editor que aceita cor solida (#RRGGBB) ou degrade linear (2 stops + angulo).
// Toggle no topo, UI muda conforme modo. onChange e disparado a cada mudanca
// pra permitir preview ao vivo no parent.
export function EditorCorOuGradiente({ valor, onChange }: Props) {
  const ehGrad = isGradient(valor);
  const [modo, setModo] = useState<'solido' | 'degrade'>(ehGrad ? 'degrade' : 'solido');
  const [grad, setGrad] = useState<DegradeParsed>(() =>
    parseGradient(valor) ?? { angle: 180, c1: HEX_RE.test(valor) ? valor : '#ffffff', c2: '#1f2937' },
  );

  // Sincroniza modo quando valor externo muda (ex: load inicial, restaurar padrao)
  useEffect(() => {
    if (isGradient(valor)) {
      setModo('degrade');
      const g = parseGradient(valor);
      if (g) setGrad(g);
    } else if (HEX_RE.test(valor)) {
      setModo('solido');
    }
  }, [valor]);

  function trocarModo(novoModo: 'solido' | 'degrade') {
    setModo(novoModo);
    if (novoModo === 'solido') {
      // Cai pra c1 do gradient atual ou um default
      const c = grad.c1 ?? '#ffffff';
      onChange(c);
    } else {
      // Vira degrade usando a cor solida atual como c1
      const c1 = HEX_RE.test(valor) ? valor : grad.c1;
      const novo: DegradeParsed = { ...grad, c1 };
      setGrad(novo);
      onChange(formatGradient(novo));
    }
  }

  function setSolido(novo: string) {
    onChange(novo);
  }

  function setGradPart(patch: Partial<DegradeParsed>) {
    const novo = { ...grad, ...patch };
    setGrad(novo);
    onChange(formatGradient(novo));
  }

  return (
    <div className="cor-ou-degrade">
      <div className="cor-ou-degrade-toggle" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={modo === 'solido'}
          className={modo === 'solido' ? 'tab tab-ativa' : 'tab'}
          onClick={() => trocarModo('solido')}
        >Solido</button>
        <button
          type="button"
          role="tab"
          aria-selected={modo === 'degrade'}
          className={modo === 'degrade' ? 'tab tab-ativa' : 'tab'}
          onClick={() => trocarModo('degrade')}
        >Degrade</button>
      </div>

      {modo === 'solido' ? (
        <div className="solido-controles">
          <input
            type="color"
            value={HEX_RE.test(valor) ? valor : '#000000'}
            onChange={(e) => setSolido(e.target.value)}
          />
          <input
            type="text"
            value={valor}
            onChange={(e) => setSolido(e.target.value)}
            maxLength={7}
            placeholder="#RRGGBB"
          />
        </div>
      ) : (
        <div className="degrade-controles">
          <div className="degrade-preview" style={{ background: formatGradient(grad) }} />
          <div className="degrade-stops">
            <label>
              <span className="muted">De</span>
              <input
                type="color"
                value={grad.c1}
                onChange={(e) => setGradPart({ c1: e.target.value })}
              />
            </label>
            <label>
              <span className="muted">Pra</span>
              <input
                type="color"
                value={grad.c2}
                onChange={(e) => setGradPart({ c2: e.target.value })}
              />
            </label>
            <label className="degrade-angle">
              <span className="muted">{grad.angle}&deg;</span>
              <input
                type="range"
                min={0}
                max={360}
                step={1}
                value={grad.angle}
                onChange={(e) => setGradPart({ angle: Number(e.target.value) })}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
